import PageNationNextIcon from '@assets/images/common/icon/pagination/next.svg';
import PageNationPrevIcon from '@assets/images/common/icon/pagination/prev.svg';
import PhotoGrayIcon from '@assets/images/common/image_dummy.svg';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { MediaTag } from '@src/api/types/mediaTag';
import Tag from '@src/ui/elements/Company/Tag';
import React, { useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

type Article = {
  to: string;
  title: string;
  content: string;
  date: string;
  category: { id: number; name: string; slug: string };
  tags: MediaTag[];
  thumbnail: string;
};

type Props = {
  articles: Article[];
};

const ArticleSlider: React.FC<Props> = ({ articles }) => {
  const splideRef = useRef<Splide>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handlePrev = () => {
    splideRef.current?.go('-1');
  };

  const handleNext = () => {
    splideRef.current?.go('+1');
  };

  const handleCategoryClick = (categorySlug: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const query = new URLSearchParams(location.search);
    query.set('category_slug', categorySlug.toString());
    query.set('page', '1');
    navigate(`?${query.toString()}`);
  };

  return (
    <div className='relative'>
      <Splide
        ref={splideRef}
        options={{
          arrows: false,
          pagination: false,
          type: 'loop',
          perPage: 1,
          gap: '1rem',
          autoplay: true,
          interval: 3000,
          pauseOnHover: false,
          breakpoints: {
            1024: {
              type: 'slide',
              rewind: true,
            },
            768: {
              type: 'loop',
              rewind: false,
            },
          },
        }}
      >
        {articles.map((article, index) => (
          <SplideSlide key={index}>
            <div className='flex h-full gap-6 transition-opacity hover:opacity-80 lg:flex-col md:gap-4'>
              <Link
                to={article.to}
                className='w-[67%] lg:w-full lg:max-w-full'
              >
                <img
                  className='aspect-[500/281] size-full object-contain'
                  src={article.thumbnail}
                  alt=''
                  width='500'
                  height='281'
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = PhotoGrayIcon;
                  }}
                  role='img'
                />
              </Link>
              <div className='flex flex-1 flex-col'>
                <div className='mb-[10px] flex items-center justify-between'>
                  <div
                    className='max-w-[70%] cursor-pointer truncate border-b-2 border-primary-500 py-1 text-xs text-primary'
                    onClick={(e) =>
                      article.category?.id && handleCategoryClick(article.category.slug, e)
                    }
                  >
                    {article.category?.name || 'カテゴリなし'}
                  </div>
                  <time
                    className='text-[10px] text-gray-400'
                    dateTime={article.date}
                  >
                    {article.date}
                  </time>
                </div>
                <Link to={article.to}>
                  <h2 className='mb-[7px] line-clamp-2 text-xl font-semibold leading-10 md:mb-[3px]'>
                    {article.title}
                  </h2>
                </Link>
                <p className='mb-[20px] line-clamp-3 text-sm md:mb-4 md:line-clamp-2 md:leading-7'>
                  {article.content}
                </p>
                <ul className='flex flex-wrap gap-2 pt-1'>
                  {article.tags.map((tag) => (
                    <Tag
                      key={tag.id}
                      label={tag.name}
                      to={`/articles?tag_slug=${tag.slug}`}
                    />
                  ))}
                </ul>
              </div>
            </div>
          </SplideSlide>
        ))}
      </Splide>

      <div className='mt-4 flex justify-center gap-4'>
        <button
          onClick={handlePrev}
          className='flex size-6 items-center justify-center rounded-full border border-gray-200 bg-gray-50'
        >
          <img
            src={PageNationPrevIcon}
            alt=''
            width={6}
            height={10}
          />
        </button>
        <button
          onClick={handleNext}
          className='flex size-6 items-center justify-center rounded-full border border-gray-200 bg-gray-50'
        >
          <img
            src={PageNationNextIcon}
            alt=''
            width={6}
            height={10}
          />
        </button>
      </div>
    </div>
  );
};

export default ArticleSlider;
